import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { EtatReservation } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Réservations — `/v1/g/:slug/reservations` (`docs/dev/api.md` § 14.6). Les
 * formes suivent `apps/api/src/reservations/commun.ts` et
 * `reservations.routes.ts` au champ près : un seul type ici, jamais un par
 * écran (même parti pris que `api/oeuvres.ts`).
 */
export interface ReservationPresentee {
  readonly id: string;
  readonly oeuvreId: string;
  readonly exemplaireId: string | null;
  readonly contactId: string;
  readonly jusquau: string;
  readonly note: string;
  readonly etat: EtatReservation;
  readonly leveeLe: string | null;
  readonly creeeLe: string;
  readonly modifieeLe: string;
}

export interface FiltresReservations {
  etat?: readonly EtatReservation[];
  oeuvreId?: string;
  contactId?: string;
  /** `true` : réservations actives dont le terme est déjà passé. */
  echues?: boolean;
  tri?: 'terme' | 'recent';
  page?: number;
  parPage?: number;
}

export interface ReponseListeReservations {
  readonly reservations: readonly ReservationPresentee[];
  readonly actives: number;
  readonly pagination: Pagination;
}

function requeteFiltres(filtres: FiltresReservations): string {
  const params = new URLSearchParams();
  if (filtres.etat && filtres.etat.length > 0) params.set('etat', filtres.etat.join(','));
  if (filtres.oeuvreId) params.set('oeuvreId', filtres.oeuvreId);
  if (filtres.contactId) params.set('contactId', filtres.contactId);
  if (filtres.echues !== undefined) params.set('echues', String(filtres.echues));
  params.set('tri', filtres.tri ?? 'terme');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 50));
  return params.toString();
}

export interface CorpsReservation {
  oeuvreId: string;
  exemplaireId?: string | null;
  contactId: string;
  jusquau: string;
  note?: string;
}

export const reservationsApi = {
  lister: (galerie: string, filtres: FiltresReservations = {}, appel?: AppelApi) =>
    api.get<ReponseListeReservations>(
      `/g/${encodeURIComponent(galerie)}/reservations?${requeteFiltres(filtres)}`,
      { galerie, ...appel },
    ),
  creer: (galerie: string, corps: CorpsReservation, appel?: AppelApi) =>
    api.post<{ reservation: ReservationPresentee }>(
      `/g/${encodeURIComponent(galerie)}/reservations`,
      corps,
      { galerie, ...appel },
    ),
};

const cleListe = (galerie: string, filtres: FiltresReservations) =>
  cleMetier(galerie, 'reservations', 'liste', filtres);

export function useReservations(galerie: string, filtres: FiltresReservations = {}) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => reservationsApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}

/** Réserver une œuvre depuis le mode foire (3.3, D5) : POST `/reservations` (14.6). */
export function useCreerReservation(galerie: string) {
  const queryClient = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsReservation, appel) => reservationsApi.creer(galerie, corps, appel),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'reservations') });
        void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres') });
      },
    }),
  );
}
